import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import WalletPayBox from '../components/WalletPayBox';
import OutreachButtons from '../components/OutreachButtons';
import { openReceiptPrint, recordPremiumPayment } from '../payments';
import { attestationToken, verifyUrl } from '../attestation';
import {
    daysUntilExpiry,
    formatGmdAmount,
    formatMsisdn,
    renewalMessage,
    splitPremium,
    toJsDate,
    useTenantWallets
} from '../wallets';
import { defaultPaymentMethod, hasWalletRails, useTenantFeatures } from '../tenantFeatures';
import { useI18n } from '../i18n';

function formatDay(value) {
    const d = toJsDate(value);
    if (!d) return '—';
    return d.toLocaleDateString('en-GB');
}

function bucketOf(days) {
    if (days === null) return 'unknown';
    if (days < 0) return 'overdue';
    if (days <= 7) return 'week';
    if (days <= 30) return 'month';
    return 'later';
}

function Renewals() {
    const { session, loading } = useCompanySession();
    const { t } = useI18n();
    const wallets = useTenantWallets(session && session.tenantId);
    const features = useTenantFeatures(session && session.tenantId);
    const [policies, setPolicies] = useState([]);
    const [ready, setReady] = useState(false);
    const [busyId, setBusyId] = useState('');
    const [message, setMessage] = useState('');
    const [pay, setPay] = useState({});

    const load = async (tenantId) => {
        const snap = await getDocs(collection(db, 'tenants', tenantId, 'policies'));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setPolicies(list);
        setReady(true);
    };

    useEffect(() => {
        if (session?.tenantId) load(session.tenantId).catch(() => setReady(true));
    }, [session]);

    const due = useMemo(() => {
        return policies
            .map((p) => ({ ...p, days: daysUntilExpiry(p) }))
            .filter((p) => p.days !== null && p.days <= 45)
            .sort((a, b) => a.days - b.days);
    }, [policies]);

    const groups = {
        overdue: due.filter((p) => bucketOf(p.days) === 'overdue'),
        week: due.filter((p) => bucketOf(p.days) === 'week'),
        month: due.filter((p) => bucketOf(p.days) === 'month')
    };

    const confirmRenew = async (policy) => {
        const state = pay[policy.id] || { method: defaultPaymentMethod(), reference: '' };
        if (String(state.method) !== 'cash' && !String(state.reference || '').trim()) {
            setMessage(t('renewals.enterConfirm'));
            return;
        }
        setBusyId(policy.id);
        setMessage('');
        try {
            const holder = policy.policyHolder || {};
            const result = await recordPremiumPayment({
                tenantId: session.tenantId,
                session,
                policy,
                customer: holder,
                wallets,
                method: state.method,
                reference: state.reference,
                extendYear: true,
                activate: true
            });
            openReceiptPrint({ receipt: result.receipt, companyName: session.companyName });
            setMessage('Renewed ' + (policy.policyNumber || policy.id) + ' · receipt ' + result.receipt.receiptNo);
            await load(session.tenantId);
        } catch (err) {
            setMessage(err.message || 'Could not renew.');
        }
        setBusyId('');
    };

    const nagText = async (policy) => {
        const holder = policy.policyHolder || {};
        let url = '';
        try {
            const token = await attestationToken(session.tenantId, policy.id);
            url = verifyUrl(token);
        } catch (err) {
            url = window.location.origin + '/verify';
        }
        return renewalMessage({
            companyName: session.companyName,
            name: holder.fullName,
            plate: policy.vehicle && policy.vehicle.registrationNumber,
            expiry: formatDay(policy.expiryDate),
            amount: splitPremium(policy).grossPremium,
            wave: formatMsisdn(wallets.pulsePayNumber || wallets.waveNumber),
            payTo: formatMsisdn(wallets.pulsePayNumber || wallets.waveNumber),
            brand: hasWalletRails(features) ? 'Pulse Pay' : 'Cash',
            cash: !hasWalletRails(features),
            url
        });
    };

    const [texts, setTexts] = useState({});
    useEffect(() => {
        let cancelled = false;
        Promise.all(due.map(async (p) => [p.id, await nagText(p)])).then((pairs) => {
            if (cancelled) return;
            const map = {};
            pairs.forEach(([id, text]) => { map[id] = text; });
            setTexts(map);
        });
        return () => { cancelled = true; };
    }, [due, session, wallets.waveNumber]);

    if (loading || !session || !ready) return <div className="p-5">{t('renewals.loading')}</div>;

    const renderGroup = (title, rows) => (
        <div className="bd-card mb-3" key={title}>
            <h5 className="mb-3">{title} <span className="badge bg-secondary">{rows.length}</span></h5>
            {rows.length === 0 ? <p className="text-muted mb-0">{t('renewals.none')}</p> : rows.map((p) => {
                const holder = p.policyHolder || {};
                const state = pay[p.id] || { method: defaultPaymentMethod(), reference: '' };
                return (
                    <div key={p.id} className="border rounded p-3 mb-3">
                        <div className="d-flex justify-content-between flex-wrap gap-2">
                            <div>
                                <div className="fw-semibold">{holder.fullName || '—'} · {p.policyNumber}</div>
                                <div className="small text-muted">
                                    {p.vehicle && p.vehicle.registrationNumber ? p.vehicle.registrationNumber + ' · ' : ''}
                                    {t('renewals.expires', { date: formatDay(p.expiryDate) })} · {p.days < 0 ? t('renewals.overdueDays', { days: Math.abs(p.days) }) : t('renewals.daysLeft', { days: p.days })} · {formatGmdAmount(splitPremium(p).grossPremium)}
                                </div>
                            </div>
                            <OutreachButtons phone={holder.phone} text={texts[p.id]} />
                        </div>
                        <div className="mt-2">
                            <WalletPayBox
                                wallets={wallets}
                                features={features}
                                policy={p}
                                method={state.method}
                                reference={state.reference}
                                onMethodChange={(method) => setPay((prev) => ({ ...prev, [p.id]: { ...state, method } }))}
                                onReferenceChange={(reference) => setPay((prev) => ({ ...prev, [p.id]: { ...state, reference } }))}
                                busy={busyId === p.id}
                                confirmLabel={t('renewals.confirm')}
                                onConfirm={() => confirmRenew(p)}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <CompanyLayout session={session}>
            <p className="text-muted">{hasWalletRails(features) ? t('renewals.intro', { wave: formatMsisdn(wallets.pulsePayNumber || wallets.waveNumber) }) : t('renewals.introCash')}</p>
            {message && <div className="alert alert-info">{message}</div>}
            {renderGroup(t('renewals.overdue'), groups.overdue)}
            {renderGroup(t('renewals.week'), groups.week)}
            {renderGroup(t('renewals.month'), groups.month)}
        </CompanyLayout>
    );
}

export default Renewals;
